import React, { FC, useCallback, useRef, useState } from "react";
import styled from "styled-components";
import { Form, FORM_FIELD_TYPES } from "site/ui";
import { AppButton } from "site/ui";
import { useProfileStore } from "store";
import { FormInstance } from "antd";
import { IUser } from "utils/types";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  font-family: "Manrope", sans-serif;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 10px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  font-family: "Manrope", sans-serif;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 20px;
  color: #666;

  &:hover {
    color: #333;
  }
`;

const ModalBody = styled.div`
  padding: 20px;
  max-height: 70vh;
  overflow-y: auto;
`;

const AvatarSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
`;

const Avatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: #5575f7;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 10px;
`;

const RemovePhotoText = styled.span`
  color: #5575f7;
  font-size: 14px;
  cursor: pointer;
  margin-top: 8px;

  &:hover {
    text-decoration: underline;
  }
`;

const HelperText = styled.p`
  font-size: 12px;
  color: #6c757d;
  margin: 4px 0 16px 0;
`;

const ButtonContainer = styled.div`
  padding: 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: center;
`;
export interface EditProfileModalProps {
  user: IUser;
  onUpdateProfile: (user: Partial<IUser>) => Promise<boolean>;
  onClose: () => void;
}
const EditProfileModal:FC<EditProfileModalProps> = ({
  user, onUpdateProfile, onClose,
}) => {
  
  const [processing, setProcessing] = useState(false)
  const [formValues, setFormValues] = useState<IUser>({ ...user });
  const [userChanges, setUserChanges] = useState<Partial<IUser>>({});
  const formRef = useRef<FormInstance<IUser>>(null);

  const handleChange = (fieldName: string, value: any) => {
    setFormValues({
      ...formValues,
      [fieldName]: value,
    });
  };


  const formItems = [
    {
      itemProps: {
        label: "First name",
        name: "firstName",
      },
      fieldType: FORM_FIELD_TYPES.TEXT,
      fieldProps: {
        placeholder: "Enter first name",
        value: formValues.firstName,
        onChange: (e: { target: { value: any } }) =>
          handleChange("firstName", e.target.value),
      },
    },
    {
      itemProps: {
        label: "Last name",
        name: "lastName",
      },
      fieldType: FORM_FIELD_TYPES.TEXT,
      fieldProps: {
        placeholder: "Enter last name",
        value: formValues.lastName,
        onChange: (e: { target: { value: any } }) =>
          handleChange("lastName", e.target.value),
      },
    },
    {
      itemProps: {
        label: "Gender",
        name: "gender",
      },
      fieldType: FORM_FIELD_TYPES.SELECT,
      fieldProps: {
        placeholder: "Select gender",
        value: formValues.gender,
        onChange: (value: any) => handleChange("gender", value),
        options: [
          { label: "Male", value: "Male" },
          { label: "Female", value: "Female" },
          { label: "Other", value: "Other" },
          { label: "Prefer not to say", value: "Prefer not to say" },
        ],
      },
    },
    {
      itemProps: {
        label: "Country",
        name: "country",
      },
      fieldType: FORM_FIELD_TYPES.SELECT,
      fieldProps: {
        placeholder: "Select country",
        value: formValues.country,
        onChange: (value: any) => handleChange("country", value),
        options: [
          { label: "Nigeria", value: "Nigeria" },
          { label: "Ghana", value: "Ghana" },
          { label: "Kenya", value: "Kenya" },
          { label: "South Africa", value: "South Africa" },
          { label: "United States", value: "United States" },
          { label: "United Kingdom", value: "United Kingdom" },
        ],
      },
    },
    {
      itemProps: {
        label: "Your profession",
        name: "profession",
      },
      fieldType: FORM_FIELD_TYPES.SELECT,
      fieldProps: {
        placeholder: "Select profession",
        value: formValues.profession,
        onChange: (value: any) => handleChange("profession", value),
        options: [
          {
            label: "Medical personnel (Doctors, Nurses, etc.)",
            value: "Medical personnel (Doctors, Nurses, etc.)",
          },
          { label: "Student", value: "Student" },
          { label: "Other", value: "Other" },
        ],
      },
    },
    {
      itemProps: {
        label: "Your main goal here",
        name: "mainGoal",
      },
      fieldType: FORM_FIELD_TYPES.SELECT,
      fieldProps: {
        placeholder: "Select your main goal",
        value: formValues.purpose,
        onChange: (value: any) => handleChange("mainGoal", value),
        options: [
          { label: "Exam preparations", value: "Exam preparations" },
          {
            label: "Professional development",
            value: "Professional development",
          },
          { label: "Career change", value: "Career change" },
          { label: "Personal interest", value: "Personal interest" },
        ],
      },
    },
  ];
  const initial = `${user?.firstName?.charAt(0)||""}${user?.lastName?.charAt(0)||""}`
  return (
    <ModalContent>
      <ModalHeader>
        <ModalTitle>Edit profile details</ModalTitle>
        <CloseButton onClick={onClose}>×</CloseButton>
      </ModalHeader>

      <ModalBody>
        <AvatarSection>
          <Avatar>{initial}</Avatar>
          <HelperText>
            Maximum size: 1MB. Supported formats: JPG, GIF, or PNG.
          </HelperText>
          <RemovePhotoText>Remove photo</RemovePhotoText>
        </AvatarSection>

        <Form
          formRef={formRef}
          formProps={{
            layout: "vertical",
            style: { padding: 0 },
            onValuesChange(changedValues) {
              setUserChanges({...userChanges, ...changedValues});
            },
          }}
          items={formItems}
        />
      </ModalBody>

      <ButtonContainer>
        <AppButton loading={processing} variant="primary" fullWidth onClick={() => {
          setProcessing(true)
          onUpdateProfile(userChanges).finally(() => setProcessing(false))
        }}>
          Save my changes
        </AppButton>
      </ButtonContainer>
    </ModalContent>
  );
};

export default EditProfileModal;
